import React, { Fragment, useContext, useEffect, useState } from "react";
import { SessionContext } from "../../../utility/useSession";
import flex from "../../../styles/flex";

import NotFoundPage from "../../error/NotFoundPage";
import { useDate } from "../../../utility/useDate";
import DateField from "../../../components/editable/EditableField/fields/DateField";
import { FiEdit } from "react-icons/fi";
import DropDownField from "../../../components/editable/EditableField/fields/DropDownField";
import { Bus, BusDefect, BusDefectStatusType, IWalkaroundDefectList, api } from "../../../logic/api";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import WaReportDownloader from "../wa_reports/WaReportDownloader";
import { WaDefectManageDialog } from "./WaDefectManageDialog";
import { isTransportManager } from "../../../utility/utils";
import { WaDefectHistoryDialog } from "./WaDefectHistoryDialog";

const defaultColumnWidth = 140;
const largeColumnWidth = 240;


interface IDefectStatusInfo {
    text: string;
    color?: string;
}

export const WaDefects: React.FC = () => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();

    if (session?.current?.site?.preferences.bus_walkaround_enabled != true) {
        return <NotFoundPage title={t("notFoundPage.notAvailable")} message={t("notFoundPage.featureNotEnabled")} />
    }

    const getBusList = api.hooks.bus.listUnpaginated();

    const { localizeDate } = useDate();

    const startDateDefault = new Date();
    startDateDefault.setHours(0);
    startDateDefault.setMinutes(0);
    startDateDefault.setSeconds(0);
    startDateDefault.setMilliseconds(0);

    const endDateDefault = new Date();
    endDateDefault.setHours(23);
    endDateDefault.setMinutes(59);
    endDateDefault.setSeconds(59);
    endDateDefault.setMilliseconds(999);

    const [startDate, setStartDate] = useState(localizeDate(startDateDefault).toISO() ?? "");
    const [endDate, setEndDate] = useState(localizeDate(endDateDefault).toISO() ?? "");

    const [selectedBus, setSelectedBus] = useState<Bus | null>({
        reg: "",
        id: 0,
        active: true,
        isVOR: false,
        distanceUnit: "miles",
    });


    const [listReportParams, setListReportParams] = useState<IWalkaroundDefectList>({
        busId: selectedBus?.id ?? 0,
        startDate: startDate,
        endDate: endDate,
        status: BusDefectStatusType.all,
    });



    //get the height of the viewport
    const [height, setHeight] = useState(window.innerHeight);
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const tableStartRef = React.useRef<HTMLDivElement>(null);
    const rowLayout = width < 1200 ? {} : flex.flexRows;
    const columnLayout = width < 1200 ? { display: "inline-block", paddingBottom: 10, width: 250, } : flex.flexAuto;
    const columnSpace = width < 1200 ? { display: "none" } : { ...flex.flexAuto, width: 20 };
    const tableHeight = height - (tableStartRef.current?.getBoundingClientRect().top ?? 0) - 70;

    const statusMap: Record<string, IDefectStatusInfo> = {
        [BusDefectStatusType.all.toString()]: {
            "text": t("walkarounds.defectStatusAll"),
        },
        [BusDefectStatusType.new.toString()]: {
            "text": t("walkarounds.defectStatusNew"),
            "color": "#eee"
        },
        [BusDefectStatusType.noAction.toString()]: {
            "text": t("walkarounds.defectStatusNoAction"),
            "color": "#eee"
        },
        [BusDefectStatusType.monitor.toString()]: {
            "text": t("walkarounds.defectStatusMonitor"),
            "color": "lightgreen"
        },
        [BusDefectStatusType.pending.toString()]: {
            "text": t("walkarounds.defectStatusPending"),
            "color": "#ffeded"
        },
        [BusDefectStatusType.resolved.toString()]: {
            "text": t("walkarounds.defectStatusResolved"),
            "color": "#eee"
        },
    };

    const [selectedDefect, setSelectedDefect] = useState<BusDefect | null>(null);
    const [showDefectDialog, setShowDefectDialog] = useState(false);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);

    return <div style={{ width: "100%", maxWidth: 2080, display: "inline-block", textAlign: "left" }}>

        {selectedDefect != null && (
            <Fragment>
                <WaDefectManageDialog
                    defect={selectedDefect}
                    show={showDefectDialog}
                    onClose={(didUpdate) => {
                        if (didUpdate == true) {
                            setListReportParams({ ...listReportParams });
                        }
                        setShowDefectDialog(false);
                    }}
                >
                </WaDefectManageDialog>
                <WaDefectHistoryDialog
                    defect={selectedDefect}
                    show={showHistoryDialog}
                    onClose={() => {
                        setShowHistoryDialog(false);
                    }}
                >
                </WaDefectHistoryDialog>
            </Fragment>
        )}

        {width >= 1200 && (
            <Fragment>
                <div style={flex.flexRows}>
                    <div style={flex.flexFill}>
                        <h2 style={{ marginTop: 0 }}>{t("walkarounds.busDefects")}</h2>
                    </div>
                </div>
                <div style={{ height: 10 }}></div>
            </Fragment>
        )}

        <div style={rowLayout}>
            <div style={columnLayout}>
                <DateField label={t("booking.labels.startDate")} value={startDate} onChange={(date) => {
                    setStartDate(date);
                    setListReportParams({
                        ...listReportParams,
                        startDate: date,
                    });
                }}></DateField>
            </div>
            <div style={columnSpace}></div>
            <div style={columnLayout}>
                <DateField label={t("booking.labels.endDate")} value={endDate} onChange={(date) => {
                    setEndDate(date);
                    setListReportParams({
                        ...listReportParams,
                        endDate: date,
                    });
                }}></DateField>
            </div>
            <div style={columnSpace}></div>
            <div style={columnLayout}>
                <DropDownField
                    minWidth={180}
                    readOnly={false}
                    label={t("walkarounds.bus")}
                    selectedItem={
                        {
                            name: selectedBus?.reg ?? "",
                            value: `${selectedBus?.id ?? ""}`,
                        }
                    }
                    onRequestData={async () => {
                        return new Promise((accept) => {
                            getBusList.send(session.current.context, {}, (b) => {
                                const buses = (b ?? []) as Bus[];
                                accept([{
                                    name: t("walkarounds.allBuses"),
                                    value: "0",
                                }, ...buses.map((bus) => {
                                    return {
                                        name: bus.reg,
                                        value: `${bus.id}`,
                                    }
                                }),]);
                            });
                        });
                    }}
                    onChange={(value) => {
                        setSelectedBus({
                            id: parseInt(value.value ?? ""),
                            reg: value.name,
                            active: true,
                            isVOR: false,
                            distanceUnit: "miles",
                        });
                        setListReportParams({
                            ...listReportParams,
                            busId: parseInt(value.value ?? ""),
                        });
                    }}
                ></DropDownField>
            </div>

            <div style={columnSpace}></div>
            <div style={columnLayout}>
                <DropDownField
                    minWidth={180}
                    readOnly={false}
                    label={t("walkarounds.busDefectStatus")}
                    selectedItem={
                        {
                            name: statusMap[listReportParams.status?.toString() ?? "1"]?.text ?? "",
                            value: `${listReportParams.status ?? 0}`,
                        }
                    }
                    onRequestData={async () => {
                        const vals = Object.values(statusMap);
                        return Object.keys(statusMap).map((key, i) => {
                            return {
                                name: key == "0" ? t("walkarounds.defectStatusAll") : vals[i].text,
                                value: key.toString(),
                            }
                        });
                    }}
                    onChange={(value) => {
                        let selectedValue = parseInt(`${value.value}`);
                        if (isNaN(selectedValue)) selectedValue = 0;
                        setListReportParams({
                            ...listReportParams,
                            status: selectedValue,
                        });
                    }}
                ></DropDownField>

            </div>

            <div style={flex.flexFill}></div>
            <div style={columnSpace}></div>
            <div style={columnLayout}>

            </div>
        </div>

        <div style={{ height: 25 }}></div>
        <div ref={tableStartRef}></div>
        <div style={{ width: "100%", height: tableHeight, overflow: "auto", border: "solid 1px #eee" }}>
            <PagedDataView<IWalkaroundDefectList, BusDefect>
                apiHook={api.hooks.walkaroundDefect.list}
                parameters={listReportParams}
                renderItem={(item) => {
                    return <WaDefectReportItem
                        defectStatusInfo={statusMap[item.defectStatus]}
                        listReportParams={listReportParams}
                        key={item.id}
                        item={item}
                        onShowHistory={() => {
                            setSelectedDefect(item);
                            setShowHistoryDialog(true);
                        }}
                        onEditDefect={() => {
                            setSelectedDefect(item);
                            setShowDefectDialog(true);
                        }} />;
                }}
                header={(_, records) => {
                    if (records.length == 0) return <div></div>;

                    const columnStyle = { padding: 20, fontWeight: "bold", backgroundColor: "white", overflow: "hidden", textOverflow: "ellipsis", };
                    return <div style={{ ...flex.flexRows, position: "sticky", top: 0, zIndex: 100 }}>

                        <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.defectRaisedDate")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.bus")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: largeColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.defectArea")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: largeColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.defectDescription")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.defectServiceable")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.defectVOR")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.defectStatus")}</div>
                        </div>
                        <div style={{ ...flex.flexAuto, width: 300 }}>
                            <div style={{ ...columnStyle }}>&nbsp;</div>
                        </div>
                    </div>;
                }}
            >
                <div style={{ padding: 20, backgroundColor: "#eee" }}>
                    {t("walkarounds.noReports")}
                </div>
            </PagedDataView>
        </div>
    </div>;
}

interface IWaDefectReportItem {
    item: BusDefect;
    listReportParams: IWalkaroundDefectList;

    defectStatusInfo?: IDefectStatusInfo;
    onEditDefect: () => void;
    onShowHistory: () => void;
}
const WaDefectReportItem: React.FC<IWaDefectReportItem> = (props) => {
    const { t } = useTranslation();
    const blueWithAlpha = "#f2f7ff";
    const rowColor = props.defectStatusInfo?.color ?? blueWithAlpha;
    const columnStyle = { paddingLeft: 20, paddingRight: 20, paddingTop: 15, paddingBottom: 10, height: 25, backgroundColor: rowColor, overflow: "hidden", textOverflow: "ellipsis", };
    const dt = useDate();
    const [showDownload, setShowDownloader] = useState(false);
    const session = useContext(SessionContext);

    return (
        <div style={{ backgroundColor: "white", marginTop: 10 }}>
            <div style={flex.flexRows}>

                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <a href="#" style={{ color: "black", }}>
                        <div style={{ ...columnStyle, textAlign: "center" }}>
                            <WaReportDownloader walkaroundResultIds={
                                [props.item.waResult.id!]
                            }
                                show={showDownload}
                                reportParams={{
                                    ...props.listReportParams,
                                    idsOnly: false
                                }}
                                callBack={() => {
                                    setShowDownloader(false);
                                }}
                            >
                                <div style={flex.flexRows} onClick={() => setShowDownloader(true)}>
                                    <div style={flex.flexFill}></div>
                                    <div style={flex.flexAuto}>{dt.toFormattedFullDateTimeString(props.item.created)}</div>
                                </div>
                            </WaReportDownloader>
                        </div>
                    </a>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.busReg ?? ""}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: largeColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.defectArea ?? ""}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: largeColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.defectDescription ?? ""}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.servicable == true ? "Y" : "N"}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.vor == true ? "Y" : "N"}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>
                        {props.defectStatusInfo?.text ?? ""}
                    </div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>
                        <a href="#" onClick={() => props.onShowHistory()} style={{ color: "black", }}>{t("walkarounds.busHistory")}</a>
                    </div>
                </div>
                {
                    isTransportManager(session) && (
                        <div style={{ ...flex.flexAuto }}>
                            <div style={{ ...columnStyle, textAlign: "center", paddingTop: 5, paddingBottom: 20, paddingRight: 5 }}>
                                <IconButton style={{
                                    height: 40,
                                    width: 40,
                                    padding: 8,
                                }} onClick={() => props.onEditDefect()}>
                                    <FiEdit />
                                </IconButton>
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    );
};
